import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Search, Plus, Ban, Loader2, Clock, Download } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

const TIPOS = [
  { id: "facto", label: "Facto" },
  { id: "decisao", label: "Decisão" },
  { id: "preferencia", label: "Preferência" },
  { id: "tarefa", label: "Tarefa" },
  { id: "aprendizagem", label: "Aprendizagem" },
];

const TYPE_STYLE = {
  facto: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  decisao: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  preferencia: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  tarefa: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  aprendizagem: "bg-sky-500/10 text-sky-400 border-sky-500/20",
};

export default function MemoryPage() {
  const [factos, setFactos] = useState([]);
  const [q, setQ] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("todos");
  const [entidadeFiltro, setEntidadeFiltro] = useState("todas");
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [novoTexto, setNovoTexto] = useState("");
  const [novoTipo, setNovoTipo] = useState("facto");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = entidadeFiltro === "todas" ? "/factos" : `/factos?entidade=${entidadeFiltro}`;
      const { data } = await api.get(url);
      setFactos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/entidades").then((r) => setEntidades(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadeFiltro]);

  const filtered = factos.filter((f) => {
    const matchQ = !q || f.texto.toLowerCase().includes(q.toLowerCase());
    const matchT = tipoFiltro === "todos" || f.tipo === tipoFiltro;
    return matchQ && matchT;
  });

  const addFact = async (e) => {
    e.preventDefault();
    const t = novoTexto.trim();
    if (!t) return;
    setAdding(true);
    try {
      const { data } = await api.post("/factos", { texto: t, tipo: novoTipo });
      setFactos((f) => [data, ...f]);
      setNovoTexto("");
      setShowAdd(false);
      toast.success("Facto adicionado à memória");
    } catch (err) {
      toast.error("Não foi possível adicionar");
    } finally {
      setAdding(false);
    }
  };

  const invalidate = async (id) => {
    await api.delete(`/factos/${id}`);
    setFactos((f) => f.filter((x) => x.id !== id));
    toast.success("Facto marcado como já não verdadeiro");
  };

  const exportar = async () => {
    try {
      const { data } = await api.get("/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `super-memoria-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Memória exportada em JSON");
    } catch (e) {
      toast.error("Não foi possível exportar");
    }
  };

  return (
    <div className="h-full overflow-y-auto px-4 lg:px-8 py-8 pl-14 lg:pl-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
              <Sparkles size={20} className="text-emerald-400" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Memória / Factos</h1>
              <p className="text-sm text-slate-400">
                {factos.length} {factos.length === 1 ? "facto vivo" : "factos vivos"} na tua memória.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="memory-export-button"
            onClick={exportar}
            className="px-3.5 py-2 rounded-lg bg-slate-800/70 hover:bg-slate-700/70 border border-slate-700 text-slate-200 font-semibold text-sm flex items-center gap-2 transition-colors"
          >
            <Download size={16} /> Exportar
          </button>
          <button
            data-testid="memory-add-fact-button"
            onClick={() => setShowAdd((v) => !v)}
            className="px-3.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors"
          >
            <Plus size={16} /> Facto
          </button>
          </div>
        </div>

        {showAdd && (
          <form
            onSubmit={addFact}
            className="mb-5 p-4 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-3 sm-fade-up"
          >
            <textarea
              data-testid="memory-new-fact-text"
              value={novoTexto}
              onChange={(e) => setNovoTexto(e.target.value)}
              rows={2}
              placeholder="Escreve um facto auto-suficiente, ex.: 'A empresa X vende em Angola através da Y.'"
              className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm resize-none"
            />
            <div className="flex items-center gap-2">
              <Select value={novoTipo} onValueChange={setNovoTipo}>
                <SelectTrigger className="w-40 bg-slate-950/60 border-slate-800 text-sm h-9" data-testid="memory-new-fact-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {TIPOS.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                data-testid="memory-save-fact-button"
                disabled={adding || !novoTexto.trim()}
                className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {adding ? <Loader2 size={15} className="animate-spin" /> : null} Guardar
              </button>
            </div>
          </form>
        )}

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="memory-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Procurar por significado ou palavra…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-slate-900/70 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
            />
          </div>
          <Select value={tipoFiltro} onValueChange={setTipoFiltro}>
            <SelectTrigger className="w-full sm:w-44 bg-slate-900/70 border-slate-800 text-sm h-[42px]" data-testid="memory-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="todos">Todos os tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entidadeFiltro} onValueChange={setEntidadeFiltro}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-900/70 border-slate-800 text-sm h-[42px]" data-testid="memory-entity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72">
              <SelectItem value="todas">Todas as entidades</SelectItem>
              {entidades.map((e) => (
                <SelectItem key={e.id} value={e.chave}>
                  {e.nome} · {e.tipo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="space-y-2">
          {loading && <div className="text-slate-500 text-sm font-mono">a carregar factos…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-14 border border-dashed border-slate-800 rounded-2xl">
              <p className="text-slate-500 text-sm">
                {factos.length === 0
                  ? "A memória está vazia. Conversa no chat e os factos aparecem aqui."
                  : "Nenhum facto corresponde à pesquisa."}
              </p>
            </div>
          )}
          {filtered.map((f) => (
            <div
              key={f.id}
              data-testid="memory-fact-card"
              className="group flex items-start gap-3 p-4 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition-colors"
            >
              <span
                className={`shrink-0 mt-0.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                  TYPE_STYLE[f.tipo] || TYPE_STYLE.facto
                }`}
              >
                {f.tipo}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 leading-relaxed">{f.texto}</p>
                <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-500 font-mono">
                  <Clock size={11} />
                  {f.criado_em ? new Date(f.criado_em).toLocaleDateString("pt-PT") : ""}
                  <span className="text-slate-700">·</span>
                  {f.fonte === "manual" ? "manual" : "auto"}
                </div>
              </div>
              <button
                data-testid="memory-fact-invalidate"
                onClick={() => invalidate(f.id)}
                title="Marcar como já não verdadeiro"
                className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-400 transition-all"
              >
                <Ban size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
